import { useMemo } from 'react';
import { useSelector } from 'store/hooks';
import usePlanHook from './usePlanHook';
import type { GetCommunityResponses } from './useGetCurrentCommunity';
import {
  FeatureIdentifiers,
  type Feature,
  type PlanDetail as Plan,
} from '../store/types';

function getFamilyKey(tokenTypeValue: string) {
  const upper = String(tokenTypeValue).toUpperCase();
  const [family] = upper.split('-');
  return family;
}

function collectFamiliesFromFeatures(features: Feature[] | undefined) {
  const families = new Set<string>();
  (features ?? [])
    .filter((f: any) => (f as any)?.identifier === FeatureIdentifiers.TGR_TYPE)
    .forEach((f: any) => {
      const md = (f as any)?.metadata;
      if (Array.isArray(md))
        md.forEach((m) => families.add(String(m).toUpperCase()));
      else if (md) families.add(String(md).toUpperCase());
    });
  return families;
}
/**
 * Provides plan-based token type restrictions derived from TGR_TYPE metadata.
 * - If TGR_TYPE includes "STAKING", enables 'staking'
 * - If TGR_TYPE includes "ERC20", enables 'ERC20' and 'ERC20-C'
 * - If TGR_TYPE is absent, nothing is disabled by default
 */
type Options = {
  // If provided, evaluate restrictions for this plan (by pk) instead of active plans.
  planId?: string;
};

export default function usePlanTokenTypeRestrictions(
  communityCheck: GetCommunityResponses,
  options: Options = {},
) {
  const { activeFeatures, plans } = usePlanHook(communityCheck);
  const platform = useSelector((state) => state.user.platform);

  // Consider only BOT and TEL#BOT plans
  const filteredPlans = useMemo(() => {
    const planPrefix = platform === 'telegram' ? 'TEL#BOT#PLAN' : 'BOT#PLAN';
    return ((plans || []).filter((p) => !p?.hide && (p?.pk || '').startsWith(planPrefix)) || []);
  }, [plans, platform]);
  const resolvedFeatures: Feature[] = useMemo(() => {
    if (!options.planId) return (activeFeatures as Feature[]) || [];
    const planIndex = new Map(filteredPlans.map((p) => [p.pk, p]));
    const visited = new Set<string>();
    const collected: Feature[] = [];

    const walk = (planId: string) => {
      if (visited.has(planId)) return;
      const plan = planIndex.get(planId);
      if (!plan) return;
      visited.add(planId);
      collected.push(...(plan.features ?? []));
      plan.features
        ?.filter((f) => f.identifier === FeatureIdentifiers.PLAN)
        .map((f) => f.metadata)
        .filter((m): m is string => typeof m === 'string')
        .forEach((childId) => walk(childId));
    };

    walk(options.planId);
    return collected;
  }, [activeFeatures, filteredPlans, options.planId]);

  // Discover all "controlled" families from ALL plans' TGR_TYPE metadata (no hardcoding)
  const controlledFamilies = useMemo(() => {
    const fams = new Set<string>();
    (filteredPlans as Plan[] | undefined)?.forEach((p) => {
      p.features
        ?.filter((f: any) => (f as any)?.identifier === FeatureIdentifiers.TGR_TYPE)
        .forEach((f: any) => {
          const md = f?.metadata;
          if (Array.isArray(md)) {
            md.forEach((m: any) => fams.add(String(m).toUpperCase()));
          } else if (md) {
            fams.add(String(md).toUpperCase());
          }
        });
    });
    return fams;
  }, [filteredPlans]);

  const allowedFamilies = useMemo(() => {
    return collectFamiliesFromFeatures(resolvedFeatures);
  }, [resolvedFeatures]);

  const isDisabledByPlan = useMemo(() => {
    return (tokenTypeValue: string) => {
      const family = getFamilyKey(tokenTypeValue);
      // Only ever affect families that are controlled via plan metadata
      if (!controlledFamilies.has(family)) return false;
      // If current plan defines TGR_TYPE, disable only controlled families not in metadata
      if (allowedFamilies.size > 0) {
        return !allowedFamilies.has(family);
      }
      // Otherwise (no TGR_TYPE on current plan), disable controlled families by default
      return true;
    };
  }, [allowedFamilies, controlledFamilies]);

  return { isDisabledByPlan };
}


